<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" ref="biddersModal" :visible="modal.show" :data="model" :title="computedTitle"
                   @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')"
                   :is-loading="loading" scrollable>
      <div class="row" v-if="errorMessage">
        <div class="col-md-12">
          <b-alert variant="danger" show>
            {{ errorMessage }}
          </b-alert>
        </div>
      </div>
      <div class="row">
        <div class="col-md-10">
          <customer-select-input-widget :model="model" attribute="customer_uuid" expand="term,trust_level"
                                        :display-field="customerDisplayField" @select="onCustomerSelect"/>
        </div>
        <div class="col-md-2 text-right" v-permission="'change_customer'">
          <b-tooltip target="new-customer-btn" :title="$t('New Customer')"/>
          <b-button variant="primary" style="margin-top: 2em" id="new-customer-btn" @click="onNewCustomerClick">
            <font-awesome-icon icon="user-plus"/>
          </b-button>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" attribute="paddle_number" type="text"/>
        </div>
        <div class="col-md-6">
          <bidder-status-select-input-widget :model="model" attribute="status"
                                             :hint="currentSale && currentSale.show_bidder_approval_status && showHint"
          />
        </div>
      </div>
      <div v-if="!enableContinuousAuction" class="row">
        <div class="col-md-6">
          <language-select-input-widget :model="model" attribute="language_uuid"/>
        </div>
        <div class="col-md-6">
          <clerk-select-input-widget :model="model" attribute="preferred_clerk_uuid" type="text"
                                     :auction="currentSale"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" attribute="skip_auction_deposit_requirement" type="checkbox" is-switch/>
        </div>
        <div class="col-md-6">
          <b-form-checkbox :checked="credit_limit_checked" size="md" class="mb-3" @change="onCreditLimitCheckedChange"
                           switch>
            {{ $t('Bidding Limit') }}
          </b-form-checkbox>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="skip_credit_card_verification" type="checkbox" is-switch/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="is_pickup" type="checkbox" is-switch/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="skip_identity_verification" type="checkbox" is-switch/>
        </div>
      </div>
      <div class="row">
        <template v-if="model.customer_uuid && credit_limit_checked">
          <div class="col-md-6">
            <currency-input-widget :autofocus="true" ref="creditLimitInput"
                                   :hint="true" :label="false"
                                   :currency="model.auction && model.auction.currency? model.auction.currency.symbol : null"
                                   @input="onCreditLimitChange"
                                   :model="model" attribute="credit_limit" type="number"/>
          </div>
          <div class="col-md-6">
            <input-widget type="select2" :model="model" v-if="enableTrustLevels" attribute="trust_level_uuid"
                          :items="trustLevels"
                          :label="false" :hint="true"
                          display-field="${item.name}"
                          value-field="value"
                          @select2Select="onTrustLevelChange"/>
          </div>
        </template>
      </div>
      <div class="row" v-if="currentSale && currentSale.type === 'live'">
         <div class="col-md-6">
          <input-widget :model="model" attribute="pre_approve_commission_bids" type="checkbox" is-switch/>
        </div>
         <div class="col-md-6">
          <input-widget :model="model" attribute="pre_approve_telephone_bids" type="checkbox" is-switch/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import InputWidget from "../../core/components/input-widget/InputWidget";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import BidderModel from "./Bidder.model";
  import CustomerSelectInputWidget from "../../core/widgets/CustomerSelectInputWidget";
  import SalesService from "../sales/SalesService";
  import {SOURCE_BIDDERS_MODAL} from "../constants";
  import CustomerService from "../customer/CustomerService";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import BidderStatusSelectInputWidget from "../../core/widgets/BidderStatusSelectInputWidget";
  import LanguageSelectInputWidget from "@/core/widgets/LanguageSelectInputWidget";
  import BiddersService from "@/modules/bidders/BiddersService";
  import {Formatter} from "../../core/services/formatter.service";
  import ClerkSelectInputWidget from "@/core/widgets/ClerkSelectInputWidget";
  import TemplatesService from "@/modules/setup/templates/TemplatesService";

  const {mapState, mapActions} = createNamespacedHelpers('bidders');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState: mapStateSales} = createNamespacedHelpers('sales');

  export default {
    name: "BiddersModal",
    components: {
      ClerkSelectInputWidget,
      LanguageSelectInputWidget,
      BidderStatusSelectInputWidget,
      CurrencyInputWidget,
      CustomerSelectInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        credit_limit_checked: false,
        model: new BidderModel(),
        errorMessage: null,
        isFirst: true,
        showHint: true,
        oldStatus: null,
        customerDisplayField: '${item.display_name} -${item.company_name ? " " + item.company_name + " -" : ""} ${item.email}'
      }
    },
    computed: {
      ...mapState({
        statuses: state => state.statuses,
        modal: state => state.modal,
        editObject: state => state.modal.object
      }),
      ...mapStateGlobal({
        defaultBidderStatus: state => state.defaultBidderStatus,
        assignLatestPaddleNumber: state => state.assignLatestPaddleNumber,
        enableTrustLevels: state => state.currentBranch.enable_trust_levels,
        enableContinuousAuction: state => state.enableContinuousAuction,
        trustLevels: state => state.currentBranch.trust_levels ? state.currentBranch.trust_levels.sort((a, b) => {
          if (a.credit_limit > b.credit_limit) {
            return 1;
          } else if (b.credit_limit > a.credit_limit) {
            return -1;
          }
          return 0;
        }).map((trustLevel) => {
          return {
            uuid: trustLevel.uuid,
            name: `${trustLevel.name} | Limit(${Formatter.asCurrency(trustLevel.credit_limit)}) | Threshold(${Formatter.asCurrency(trustLevel.threshold)})`,
            value: trustLevel.uuid,
            exclude_from_auto_assignment: trustLevel.exclude_from_auto_assignment,
            credit_limit: trustLevel.credit_limit
          }
        }) : []
      }),
      ...mapStateSales({
        currentSale: state => state.view.currentSale
      }),
      computedTitle() {
        if (this.model.auction) {
          if (this.model.uuid) {
            return this.$i18n.t('Update Bidder for {saleNo}', {saleNo: this.model.auction.sale_no})
          } else {
            return this.$i18n.t('New Bidder for {saleNo}', {saleNo: this.model.auction.sale_no})
          }
        }
        return '';
      }
    },
    watch: {
      async editObject() {


        if (this.editObject && (this.editObject.uuid || this.editObject.auction_uuid)) {

          this.model = new BidderModel(this.editObject);
          //await this.initPaddleNumber();

          if (this.model.paddle_number && !this.model.customer_uuid) {
            this.$refs.biddersModal.updateOldState();
          }
          this.oldStatus = this.model.status;
        }
        if (this.editObject.credit_limit) {
          this.onCreditLimitChange(this.editObject.credit_limit);
        }
        if (!this.editObject.uuid) {
          await this.getLatestPaddleNumber()
        }
      },
      'model.customer_uuid': {
        deep: true,
        async handler() {
          if (this.model && this.model.customer_uuid) {
            this.errorMessage = null;
            let {success, body} = await CustomerService.getCustomer(this.model.customer_uuid, 'term,trust_level');

            if (success) {
              if (this.model.uuid) {
                if (!this.isFirst) {
                  this.model.credit_limit = this.getCreditLimit(body);
                  this.model.skip_auction_deposit_requirement = body.skip_auction_deposit_requirement;
                  this.model.skip_credit_card_verification = body.skip_credit_card_verification;
                  this.model.pre_approve_commission_bids = body.term.pre_approve_commission_bids;
                  this.model.pre_approve_telephone_bids = body.term.pre_approve_telephone_bids;
                  this.model.skip_identity_verification = body.skip_identity_verification;
                  this.credit_limit_checked = !!this.model.credit_limit;
                } else {
                  this.isFirst = false;
                }
              } else {
                this.model.credit_limit = this.getCreditLimit(body);
                this.model.skip_auction_deposit_requirement = body.skip_auction_deposit_requirement;
                this.model.skip_credit_card_verification = body.skip_credit_card_verification;
                this.model.pre_approve_commission_bids = body.term.pre_approve_commission_bids;
                this.model.pre_approve_telephone_bids = body.term.pre_approve_telephone_bids;
                this.model.skip_identity_verification = body.skip_identity_verification;
                this.credit_limit_checked = !!this.model.credit_limit;
              }
            }

            // if (success && !body.term.identification_checked) {
            //   this.errorMessage = this.$i18n.t('Identification check not approved for following customer.');
            // }
          }
        }
      }
    },
    methods: {
      ...mapActions(['hideModal', 'createBidder', 'updateBidder']),
      ...customerMapActions({
        showCustomerModal: 'showModal'
      }),
      async onShow() {
        if (this.model.credit_limit) this.credit_limit_checked = true;
        this.showConfirmation = true;
      },
      async getLatestPaddleNumber() {
        if (this.assignLatestPaddleNumber && this.currentSale) {
          let response = await BiddersService.getLatestPaddleNumber(this.currentSale.uuid)
          if (response.success) {
            this.model.paddle_number = response.body.latest_paddle_number ? parseInt(response.body.latest_paddle_number) + 1 : 1
          }
        }
      },
      onHidden() {
        this.model = new BidderModel();
        this.isFirst = true;
        this.hideModal();
        this.errorMessage = null;
        this.showHint = true
      },
      async onSubmit() {
        if (this.currentSale && this.currentSale.manual_bidder_approval && this.model.uuid && this.oldStatus !== 'approved' && this.model.status === 'approved') {
          let success = await this.$confirm(this.$i18n.t("This bidder does not have deposit available for this auction. Are you sure you would like to approve them?"))
          if (!success) {
            return;
          }
        }

        if (this.oldStatus !== 'pending' && this.model.status === 'pending') {
          this.loading = true
          let success = await this.$confirm(this.$i18n.t('Setting the bidder status to pending will trigger an automated notification to be sent to them. You can check this notification in the Setup > Notifications - Pending Bidder Notification section.<br><br>Are you sure you would like to proceed?'))
          if (!success) {
            this.loading = false
            return
          }
        }

        let response;
        this.loading = true;

        this.model.auction_uuid = this.model.auction.uuid;

        // Generate bidder status automatically if not exist
        if (!this.model.status) {
          this.model.status = this.defaultBidderStatus
        }

        if (!this.model.uuid) {
          response = await this.createBidder({
            ...this.model.toJSON(), created_manually: true
          });
        } else {
          response = await this.updateBidder(this.model);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hideModal();
          if (!this.model.uuid) {
            this.$toast('success', this.$t('Bidder was successfully created'));
          } else {
            this.$toast('success', this.$t('Bidder was successfully updated'));
          }
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            if (response.body.status) {
              this.showHint = false
            }
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      onNewCustomerClick() {
        this.showCustomerModal({
          source: SOURCE_BIDDERS_MODAL
        })
      },
      onCustomerSelect(customer) {
        this.model.is_pickup = customer && customer.term && customer.term.is_pickup
      },
      onCreditLimitCheckedChange() {
        this.credit_limit_checked = !this.credit_limit_checked;
        if (!this.credit_limit_checked) this.model.credit_limit = null;
      },
      async initPaddleNumber() {
        if (!this.model.uuid) {
          let response = await SalesService.getSaleInfo(this.model.auction.uuid);
          if (response.body.last_paddle_number) {
            this.model.paddle_number = parseInt(response.body.last_paddle_number) + 1;
          } else {
            this.model.paddle_number = this.model.auction.starting_paddle_number;
          }
          return response;
        }
      },
      getCreditLimit(customer) {
        if (!this.enableTrustLevels) {
          return customer.term.credit_limit;
        }
        if (!customer.trust_level) {
          return null;
        }
        const trustLevel = this.trustLevels.find(tr => tr.uuid === customer.trust_level.trust_level_uuid);
        if (!trustLevel) {
          return null;
        }
        this.model.trust_level_uuid = trustLevel.uuid;
        return trustLevel.credit_limit;
      },
      onTrustLevelChange(data) {
        this.model.credit_limit = data.credit_limit;
      },
      onCreditLimitChange(event) {
        if (!this.enableTrustLevels || !this.trustLevels) {
          return;
        }
        const trustLevel = this.trustLevels.find(tr => tr.credit_limit == event);
        if (trustLevel) {
          this.model.trust_level_uuid = trustLevel.uuid;
        }
      }
    }
  }
</script>

<style scoped>

</style>
